import type { PartialDeal } from "../../types/deal";
import { TENANT_FIELD_CONFIG } from "../../constants/tenantFields";
import type { ExtractionResult } from "../../types/extraction";
import { normalizeDealData } from "../../utils/dealValidation";
import { filterDealDataForExtractionMerge } from "../../utils/extractionMerge";

function makeTempId(prefix: string, index: number) {
  return `tmp-${prefix}-${Date.now()}-${index}-${Math.random().toString(36).slice(2, 8)}`;
}

export function hasFilledExtractionValue(value: unknown): boolean {
  if (value === null || value === undefined) return false;
  if (typeof value === "string" && value.trim() === "") return false;
  if (Array.isArray(value) && value.length === 0) return false;
  return true;
}

/** Overlay non-empty extraction deal fields onto a base deal (defaults + empty form). */
export function mergeExtractionDeal(
  base: PartialDeal,
  extractionResult: ExtractionResult,
): PartialDeal {
  const extractionDeal = filterDealDataForExtractionMerge(
    (extractionResult.deal || {}) as Record<string, unknown>,
    {
      isTermSheet: extractionResult.isTermSheet,
      isInitialMaterials: extractionResult.isInitialMaterials,
    },
  );

  const { contacts: _contacts, ...dealWithoutContacts } = extractionDeal as any;

  if (
    dealWithoutContacts.source &&
    typeof dealWithoutContacts.source === "object" &&
    dealWithoutContacts.source !== null
  ) {
    dealWithoutContacts.source_entity = dealWithoutContacts.source;
  }

  const normalized = normalizeDealData(dealWithoutContacts);
  const merged: PartialDeal = { ...base };

  Object.keys(normalized).forEach((key) => {
    const extractionValue = (normalized as any)[key];
    if (hasFilledExtractionValue(extractionValue)) {
      (merged as any)[key] = extractionValue;
    }
  });

  return merged;
}

export function assetsFromExtraction(
  extractionResult: ExtractionResult,
  dealId?: number | null,
) {
  if (extractionResult.isInitialMaterials) return [];

  const incoming = extractionResult.assets || [];
  return incoming.map((asset: any, index: number) => {
    const { shouldCreate: _shouldCreate, ...assetWithoutFlag } = asset || {};
    const next: any = {
      ...assetWithoutFlag,
      asset_name: asset?.asset_name || asset?.name || `Asset ${index + 1}`,
    };
    if (!next.id || typeof next.id !== "number") {
      next.id = makeTempId("asset", index);
    }
    if (dealId) {
      next.deal = { id: dealId };
      next.deal_id = dealId;
    }
    return next;
  });
}

function numericAssetId(asset: any): number | null {
  if (typeof asset?.id === "number") return asset.id;
  if (typeof asset?.asset_id === "number") return asset.asset_id;
  return null;
}

function assetNameKey(asset: any) {
  return typeof asset?.asset_name === "string"
    ? asset.asset_name.toLowerCase().trim()
    : "";
}

function findMatchingAssetIndex(
  list: any[],
  target: any,
  used: Set<number>,
) {
  const targetId = numericAssetId(target);
  if (targetId != null) {
    const byId = list.findIndex(
      (row, index) =>
        !used.has(index) && numericAssetId(row) === targetId,
    );
    if (byId >= 0) return byId;
  }

  const name = assetNameKey(target);
  if (!name) return -1;
  return list.findIndex(
    (row, index) => !used.has(index) && assetNameKey(row) === name,
  );
}

/** Overlay extraction assets onto existing deal assets; append unmatched new rows. */
export function mergeIncomingAssets(existing: any[], incoming: any[]) {
  if (!existing?.length) return incoming;
  if (!incoming?.length) return existing;

  const usedIncoming = new Set<number>();
  const next = existing.map((current) => {
    const foundIndex = findMatchingAssetIndex(incoming, current, usedIncoming);
    if (foundIndex < 0) return { ...current };
    usedIncoming.add(foundIndex);
    const found = incoming[foundIndex];
    return {
      ...current,
      ...found,
      id: typeof current.id === "number" ? current.id : found.id,
    };
  });

  incoming.forEach((row, index) => {
    if (!usedIncoming.has(index)) next.push(row);
  });
  return next;
}

/** Merge extraction contacts into existing contacts by id, then email, then name+company. */
export function mergeIncomingContacts(existing: any[], incoming: any[]) {
  if (!incoming?.length) return existing || [];
  const merged = [...(existing || [])];

  incoming.forEach((incomingContact: any) => {
    const email =
      typeof incomingContact.contact_email === "string"
        ? incomingContact.contact_email.trim().toLowerCase()
        : "";
    const name =
      typeof incomingContact.contact_name === "string"
        ? incomingContact.contact_name.trim().toLowerCase()
        : "";
    const company =
      typeof incomingContact.company === "string"
        ? incomingContact.company.trim().toLowerCase()
        : "";

    const existingIndex = merged.findIndex((contact: any) => {
      if (
        typeof incomingContact?.id === "number" &&
        typeof contact?.id === "number"
      ) {
        return incomingContact.id === contact.id;
      }
      const existingEmail =
        typeof contact.contact_email === "string"
          ? contact.contact_email.trim().toLowerCase()
          : "";
      const existingName =
        typeof contact.contact_name === "string"
          ? contact.contact_name.trim().toLowerCase()
          : "";
      const existingCompany =
        typeof contact.company === "string"
          ? contact.company.trim().toLowerCase()
          : "";
      if (email && existingEmail) return email === existingEmail;
      if (name && existingName) {
        return name === existingName && company === existingCompany;
      }
      return false;
    });

    if (existingIndex >= 0) {
      const current = { ...merged[existingIndex] };
      Object.keys(incomingContact).forEach((key) => {
        if (hasFilledExtractionValue((incomingContact as any)[key])) {
          (current as any)[key] = (incomingContact as any)[key];
        }
      });
      merged[existingIndex] = current;
    } else {
      merged.push(incomingContact);
    }
  });

  return merged;
}

export function mergeIncomingTenants(existing: any[], incoming: any[]) {
  if (!existing?.length) return incoming;
  if (!incoming?.length) return existing;
  return [...existing, ...incoming];
}

/**
 * Swap in a refreshed extraction preview without touching other tenant rows.
 * Only rows created by the previous extraction (by id) are replaced; persisted
 * tenants (numeric ids) and manually added rows are always kept, so a preview
 * refresh can never make the update diff treat them as removed.
 */
export function replaceExtractionTenants(
  current: any[],
  previousExtractionIds: ReadonlySet<unknown>,
  incoming: any[],
) {
  const kept = (current || []).filter(
    (tenant) =>
      typeof tenant?.id === "number" || !previousExtractionIds.has(tenant?.id),
  );
  return [...kept, ...(incoming || [])];
}

export function contactsFromExtraction(extractionResult: ExtractionResult) {
  const extractionDeal = (extractionResult.deal || {}) as any;
  const incoming =
    extractionDeal.contacts || (extractionResult as any).contacts || [];

  if (!Array.isArray(incoming)) return [];

  return incoming.map((contact: any, index: number) => {
    const next = {
      ...contact,
      contact_name:
        contact?.contact_name || contact?.name || `Contact ${index + 1}`,
      shouldCreate: contact?.shouldCreate !== false,
      shouldLink: contact?.shouldLink !== false,
    };
    if (!next.id || typeof next.id !== "number") {
      next.id = makeTempId("contact", index);
    }
    return next;
  });
}

function isUkDeal(deal: PartialDeal) {
  return (
    deal?.countries?.some(
      (country: any) =>
        country?.name === "United Kingdom" ||
        country?.nicename === "United Kingdom" ||
        country?.id === 225,
    ) ?? false
  );
}

function matchedAssetNameByRow(
  extractionResult: ExtractionResult,
): Map<number, string> {
  const map = new Map<number, string>();
  const matchedAssets = extractionResult.matchedAssets as Record<
    string,
    { data?: any[]; row_indices?: number[] }
  > | null;

  if (!matchedAssets) return map;

  Object.entries(matchedAssets).forEach(([assetName, details]) => {
    if (Array.isArray(details?.data)) {
      details.data.forEach((row: any) => {
        if (row && typeof row.row_index === "number") {
          const name =
            (typeof row.matched_asset === "string" && row.matched_asset) ||
            assetName ||
            "";
          map.set(row.row_index, name);
        }
      });
    }
    if (Array.isArray(details?.row_indices)) {
      details.row_indices.forEach((rowIndex) => {
        if (typeof rowIndex === "number") {
          map.set(rowIndex, assetName || "");
        }
      });
    }
  });

  return map;
}

function findAssetByName(assets: any[], name: string) {
  const normalized = name.toLowerCase().trim();
  if (!normalized) return undefined;
  return assets.find(
    (asset: any) => asset.asset_name?.toLowerCase().trim() === normalized,
  );
}

/** Build tenant rows from Excel tenant_dataframe and/or tenants nested on assets (PDF). */
export function tenantsFromExtraction(
  extractionResult: ExtractionResult,
  assets: any[],
  deal: PartialDeal,
) {
  const areaType = isUkDeal(deal) ? "SQFT" : "SQM";
  const dealCurrency = deal.loan_amount_currency || "GBP";
  const rowAssetNames = matchedAssetNameByRow(extractionResult);
  const tenants: any[] = [];
  let index = 0;

  const tenantData = extractionResult.tenantData;
  if (Array.isArray(tenantData) && tenantData.length > 0) {
    tenantData.forEach((tenantRow: any, rowIndexFallback: number) => {
      const rowIndex =
        typeof tenantRow?.row_index === "number"
          ? tenantRow.row_index
          : rowIndexFallback;
      const matchedAssetName =
        rowAssetNames.get(rowIndex) ||
        (typeof tenantRow.matched_asset === "string"
          ? tenantRow.matched_asset
          : "") ||
        tenantRow.asset_name ||
        "";
      const matchedAsset = findAssetByName(assets, matchedAssetName);

      tenants.push({
        id: makeTempId("tenant", index),
        asset_id: matchedAsset?.id ? matchedAsset.id : undefined,
        shouldCreate: true,
        included: true,
        tenant_name: tenantRow.tenant_name || "",
        tenant_sector: tenantRow.tenant_sector || "",
        lease_start_date: tenantRow.lease_start_date || null,
        lease_end_date: tenantRow.lease_end_date || null,
        break_option_date: tenantRow.break_option_date || null,
        next_review_date: tenantRow.next_review_date || null,
        contracted_rent: tenantRow.contracted_rent || null,
        passing_rent: tenantRow.passing_rent || null,
        rent_free_amount: tenantRow.rent_free_amount || null,
        rent_free_end_date: tenantRow.rent_free_end_date || null,
        rent_currency: tenantRow.rent_currency || dealCurrency,
        total_area: tenantRow.total_area || null,
        area_type: tenantRow.area_type || areaType,
        estimated_rental_value: tenantRow.estimated_rental_value || null,
        tf_erv: tenantRow.tf_erv || null,
        unit_type: tenantRow.unit_type || null,
        created_at: new Date().toISOString(),
        updated_at: null,
        asset: matchedAsset?.id ? matchedAsset : undefined,
        asset_name: matchedAsset?.asset_name || matchedAssetName || "",
      });
      index += 1;
    });
  }

  if (Array.isArray(extractionResult.assets)) {
    extractionResult.assets.forEach((assetData: any) => {
      if (!Array.isArray(assetData.tenants) || assetData.tenants.length === 0) {
        return;
      }
      const assetName = assetData.asset_name || "";
      const matchedAsset = findAssetByName(assets, assetName);

      const emptyToNull = (value: any) => {
        if (!value || (typeof value === "string" && value.trim() === "")) {
          return null;
        }
        return value;
      };

      assetData.tenants.forEach((tenantRow: any) => {
        tenants.push({
          id: makeTempId("tenant", index),
          asset_id: matchedAsset?.id ? matchedAsset.id : undefined,
          shouldCreate: true,
          included: true,
          tenant_name: tenantRow.tenant_name || "",
          tenant_sector: tenantRow.tenant_sector || "",
          lease_start_date: emptyToNull(tenantRow.lease_start_date),
          lease_end_date: emptyToNull(tenantRow.lease_end_date),
          break_option_date: emptyToNull(tenantRow.break_option_date),
          next_review_date: emptyToNull(tenantRow.next_review_date),
          contracted_rent: tenantRow.contracted_rent || null,
          passing_rent: tenantRow.passing_rent || null,
          rent_free_amount: tenantRow.rent_free_amount || null,
          rent_free_end_date: emptyToNull(tenantRow.rent_free_end_date),
          rent_currency: tenantRow.rent_currency || dealCurrency,
          total_area: tenantRow.total_area || null,
          area_type: tenantRow.area_type || areaType,
          estimated_rental_value: tenantRow.estimated_rental_value || null,
          tf_erv: tenantRow.tf_erv || null,
          unit_type: tenantRow.unit_type || null,
          created_at: new Date().toISOString(),
          updated_at: null,
          asset: matchedAsset?.id ? matchedAsset : undefined,
          asset_name: matchedAsset?.asset_name || assetName || "",
        });
        index += 1;
      });
    });
  }

  return tenants;
}

const DEFAULT_ROW_RANGE = { start: 10, end: 80 };

export function parseExcelRange(range?: string | null) {
  if (!range) return null;
  const match = range.match(/^([A-Za-z]+)(\d+):([A-Za-z]+)(\d+)$/);
  if (!match) return null;
  return {
    startColumn: match[1].toUpperCase(),
    startRow: parseInt(match[2], 10),
    endColumn: match[3].toUpperCase(),
    endRow: parseInt(match[4], 10),
  };
}

export function getInitialTenantRowRange(extractionResult: ExtractionResult) {
  for (const range of extractionResult.tenantColumnRanges || []) {
    const parsed = parseExcelRange(range.cell_range);
    if (parsed) {
      return { start: parsed.startRow, end: parsed.endRow };
    }
  }
  return { ...DEFAULT_ROW_RANGE };
}

export function getInitialColumnOverrides(extractionResult: ExtractionResult) {
  const overrides: Record<string, string> = {};
  TENANT_FIELD_CONFIG.forEach(({ field }) => {
    const fieldName = field as string;
    const baseRange = (extractionResult.tenantColumnRanges || []).find(
      (range) => range.field_name === fieldName,
    );
    const parsed = parseExcelRange(baseRange?.cell_range);
    overrides[fieldName] = parsed?.startColumn || "";
  });
  return overrides;
}

export function getColumnLetterForField(
  field: string,
  overrides: Record<string, string>,
  ranges?: ExtractionResult["tenantColumnRanges"],
) {
  const overrideLetter = overrides[field];
  if (overrideLetter && overrideLetter.trim()) {
    return overrideLetter.trim().toUpperCase();
  }
  const baseRange = ranges?.find((range) => range.field_name === field);
  const parsed = parseExcelRange(baseRange?.cell_range);
  return parsed?.startColumn || "";
}

export { DEFAULT_ROW_RANGE };
